import { supabase } from '../lib/supabase';

// Banco de Talentos: candidatos a vagas abertas e leads guardados para contratação futura
// (tabelas talents e talent_interactions; RLS por talent.access).

/** Empresa que usa o Banco de Talentos por padrão (Evolua Negócios). Outras ligam pelo módulo 'banco-talentos'. */
export const TALENT_BANK_COMPANY_ID = 29;
export const TALENT_BANK_MODULE = 'banco-talentos';

export const canSeeTalentBank = (company: { id?: number | null; modules_enabled?: string[] | null } | null | undefined) =>
  !!company && (company.id === TALENT_BANK_COMPANY_ID
    || (Array.isArray(company.modules_enabled) && company.modules_enabled.includes(TALENT_BANK_MODULE)));

export type TalentKind = 'candidato' | 'futuro';
export type TalentStage = 'novo' | 'triagem' | 'entrevista' | 'proposta' | 'banco' | 'contratado' | 'descartado';
export type InteractionKind = 'nota' | 'ligacao' | 'whatsapp' | 'email' | 'entrevista' | 'etapa';

export const TALENT_KIND_LABEL: Record<TalentKind, string> = {
  candidato: 'Candidato (vaga aberta)',
  futuro: 'Lead para contratação futura',
};

export const TALENT_STAGES: TalentStage[] = ['novo', 'triagem', 'entrevista', 'proposta', 'banco', 'contratado', 'descartado'];

export const TALENT_STAGE_LABEL: Record<TalentStage, string> = {
  novo: 'Novo',
  triagem: 'Em triagem',
  entrevista: 'Entrevista',
  proposta: 'Proposta',
  banco: 'Guardado no banco',
  contratado: 'Contratado',
  descartado: 'Descartado',
};

export const INTERACTION_LABEL: Record<InteractionKind, string> = {
  nota: 'Anotação',
  ligacao: 'Ligação',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  entrevista: 'Entrevista',
  etapa: 'Mudança de etapa',
};

export const TALENT_AREAS = ['Comercial', 'Atendimento', 'Marketing', 'Operação', 'Financeiro', 'Tecnologia', 'Gestão', 'Outra'];
export const TALENT_SOURCES = ['Indicação', 'LinkedIn', 'Instagram', 'Site', 'Currículo espontâneo', 'Evento', 'Outra'];

export interface Talent {
  id: string;
  company_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  kind: TalentKind;
  stage: TalentStage;
  area: string | null;
  position_interest: string | null;
  source: string | null;
  linkedin_url: string | null;
  resume_url: string | null;
  salary_expectation: number | null;
  availability: string | null;
  skills: string[];
  rating: number | null;
  notes: string | null;
  next_contact_date: string | null;
  discard_reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TalentInteraction {
  id: string;
  talent_id: string;
  kind: InteractionKind;
  body: string;
  created_by: string | null;
  created_at: string;
}

export interface TalentFormData {
  name: string;
  email: string;
  phone: string;
  city: string;
  kind: TalentKind;
  stage: TalentStage;
  area: string;
  position_interest: string;
  source: string;
  linkedin_url: string;
  resume_url: string;
  salary_expectation: number | null;
  availability: string;
  skills: string;
  rating: number | null;
  notes: string;
  next_contact_date: string;
  discard_reason: string;
}

export const emptyTalentForm = (over: Partial<TalentFormData> = {}): TalentFormData => ({
  name: '', email: '', phone: '', city: '',
  kind: 'futuro', stage: 'novo',
  area: '', position_interest: '', source: '',
  linkedin_url: '', resume_url: '',
  salary_expectation: null, availability: '',
  skills: '', rating: null, notes: '',
  next_contact_date: '', discard_reason: '',
  ...over,
});

export const talentToForm = (t: Talent): TalentFormData => ({
  name: t.name,
  email: t.email ?? '',
  phone: t.phone ?? '',
  city: t.city ?? '',
  kind: t.kind,
  stage: t.stage,
  area: t.area ?? '',
  position_interest: t.position_interest ?? '',
  source: t.source ?? '',
  linkedin_url: t.linkedin_url ?? '',
  resume_url: t.resume_url ?? '',
  salary_expectation: t.salary_expectation,
  availability: t.availability ?? '',
  skills: (t.skills || []).join(', '),
  rating: t.rating,
  notes: t.notes ?? '',
  next_contact_date: t.next_contact_date ?? '',
  discard_reason: t.discard_reason ?? '',
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Primeira mensagem de erro do formulário, ou null quando está tudo válido. */
export function validateTalent(form: TalentFormData): string | null {
  if (!form.name.trim()) return 'Informe o nome.';
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) return 'E-mail inválido.';
  if (!form.email.trim() && form.phone.replace(/\D/g, '').length < 8) return 'Informe ao menos um contato: e-mail ou telefone.';
  if (form.salary_expectation != null && form.salary_expectation < 0) return 'A pretensão salarial não pode ser negativa.';
  if (form.stage === 'descartado' && !form.discard_reason.trim()) return 'Informe o motivo do descarte.';
  for (const url of [form.linkedin_url, form.resume_url]) {
    if (url.trim() && !/^https?:\/\//i.test(url.trim())) return 'Os links precisam começar com http:// ou https://.';
  }
  return null;
}

export const parseSkills = (raw: string): string[] =>
  Array.from(new Set(raw.split(/[,;\n]/).map(s => s.trim()).filter(Boolean)));

export interface TalentFilters {
  search: string;
  stage: TalentStage | 'todos';
  kind: TalentKind | 'todos';
  area: string;
  /** Só quem tem contato marcado até hoje (vencido ou do dia). */
  onlyDue: boolean;
}

export const emptyTalentFilters = (): TalentFilters => ({ search: '', stage: 'todos', kind: 'todos', area: '', onlyDue: false });

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const isContactDue = (t: Talent, today: string): boolean =>
  !!t.next_contact_date && t.next_contact_date <= today && t.stage !== 'contratado' && t.stage !== 'descartado';

export function filterTalents(list: Talent[], f: TalentFilters, today: string): Talent[] {
  const q = normalize(f.search.trim());
  return list.filter(t => {
    if (f.stage !== 'todos' && t.stage !== f.stage) return false;
    if (f.kind !== 'todos' && t.kind !== f.kind) return false;
    if (f.area && t.area !== f.area) return false;
    if (f.onlyDue && !isContactDue(t, today)) return false;
    if (!q) return true;
    const hay = normalize([t.name, t.email, t.phone, t.city, t.position_interest, t.area, ...(t.skills || [])].filter(Boolean).join(' '));
    return hay.includes(q);
  });
}

export interface TalentSummary {
  total: number;
  futuro: number;
  candidatos: number;
  emProcesso: number;
  contatosVencidos: number;
  contratados: number;
}

export function summarizeTalents(list: Talent[], today: string): TalentSummary {
  const ativos = list.filter(t => t.stage !== 'contratado' && t.stage !== 'descartado');
  return {
    total: ativos.length,
    futuro: ativos.filter(t => t.kind === 'futuro').length,
    candidatos: ativos.filter(t => t.kind === 'candidato').length,
    emProcesso: list.filter(t => ['triagem', 'entrevista', 'proposta'].includes(t.stage)).length,
    contatosVencidos: list.filter(t => isContactDue(t, today)).length,
    contratados: list.filter(t => t.stage === 'contratado').length,
  };
}

function friendlyError(error: { code?: string; message?: string }): Error {
  if (error.code === '23505') return new Error('Já existe uma pessoa cadastrada com este e-mail.');
  return new Error(error.message || 'Erro ao salvar no Banco de Talentos.');
}

export async function fetchTalents(companyId: number): Promise<Talent[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from('talents').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Talent[];
}

export async function saveTalent(
  companyId: number,
  existing: Talent | null,
  form: TalentFormData,
  userId?: string,
): Promise<Talent> {
  if (!supabase) throw new Error('Supabase não configurado.');
  const erro = validateTalent(form);
  if (erro) throw new Error(erro);

  const payload = {
    name: form.name.trim(),
    email: form.email.trim().toLowerCase() || null,
    phone: form.phone.replace(/\D/g, '') || null,
    city: form.city.trim() || null,
    kind: form.kind,
    stage: form.stage,
    area: form.area.trim() || null,
    position_interest: form.position_interest.trim() || null,
    source: form.source.trim() || null,
    linkedin_url: form.linkedin_url.trim() || null,
    resume_url: form.resume_url.trim() || null,
    salary_expectation: form.salary_expectation,
    availability: form.availability.trim() || null,
    skills: parseSkills(form.skills),
    rating: form.rating,
    notes: form.notes.trim() || null,
    next_contact_date: form.next_contact_date || null,
    discard_reason: form.stage === 'descartado' ? form.discard_reason.trim() : null,
  };

  if (existing) {
    const { data, error } = await supabase.from('talents').update(payload).eq('id', existing.id).select('*').single();
    if (error) throw friendlyError(error);
    if (existing.stage !== form.stage) {
      await addInteraction(companyId, existing.id, 'etapa', `${TALENT_STAGE_LABEL[existing.stage]} → ${TALENT_STAGE_LABEL[form.stage]}`, userId);
    }
    return data as Talent;
  }
  const { data, error } = await supabase.from('talents')
    .insert([{ ...payload, company_id: companyId, created_by: userId ?? null }]).select('*').single();
  if (error) throw friendlyError(error);
  return data as Talent;
}

export async function moveTalentStage(companyId: number, talent: Talent, stage: TalentStage, userId?: string): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  if (talent.stage === stage) return;
  if (stage === 'descartado') throw new Error('Para descartar, edite a pessoa e informe o motivo.');
  const { error } = await supabase.from('talents').update({ stage, discard_reason: null }).eq('id', talent.id);
  if (error) throw error;
  await addInteraction(companyId, talent.id, 'etapa', `${TALENT_STAGE_LABEL[talent.stage]} → ${TALENT_STAGE_LABEL[stage]}`, userId);
}

export async function deleteTalent(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  const { error } = await supabase.from('talents').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchInteractions(talentId: string): Promise<TalentInteraction[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from('talent_interactions').select('id, talent_id, kind, body, created_by, created_at')
    .eq('talent_id', talentId).order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as TalentInteraction[];
}

export async function addInteraction(
  companyId: number,
  talentId: string,
  kind: InteractionKind,
  body: string,
  userId?: string,
): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  if (!body.trim()) throw new Error('Escreva o que aconteceu.');
  const { error } = await supabase.from('talent_interactions')
    .insert([{ company_id: companyId, talent_id: talentId, kind, body: body.trim(), created_by: userId ?? null }]);
  if (error) throw error;
}

export async function setNextContact(id: string, date: string | null): Promise<void> {
  if (!supabase) throw new Error('Supabase não configurado.');
  const { error } = await supabase.from('talents').update({ next_contact_date: date }).eq('id', id);
  if (error) throw error;
}
